import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, Typography, Tooltip } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TuneIcon from '@mui/icons-material/Tune';
import LogoutIcon from '@mui/icons-material/Logout';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Admin from './Admin';
import Monitor from './Monitor';
import Login from './Login';
import axios from 'axios';

const SIDEBAR_W = 220;
const S = {
  sidebar: {
    width: SIDEBAR_W,
    flexShrink: 0,
    bgcolor: '#0f172a',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 1200,
  },
  logo: {
    px: 3,
    py: 2.5,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    mb: 1,
  },
  navItem: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    py: 1.25,
    mx: 1.5,
    mb: 0.5,
    borderRadius: 1.5,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s',
    bgcolor: active ? 'rgba(59,130,246,0.18)' : 'transparent',
    borderLeft: active ? '3px solid #3b82f6' : '3px solid transparent',
    color: active ? '#f8fafc' : '#94a3b8',
    '&:hover': {
      bgcolor: active ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.06)',
      color: '#f8fafc',
    },
  }),
};

const NavItem = ({ icon, label, active, onClick }) => (
  <Box sx={S.navItem(active)} onClick={onClick}>
    {React.cloneElement(icon, { sx: { fontSize: 18, opacity: active ? 1 : 0.7 } })}
    <Typography sx={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400, lineHeight: 1 }}>
      {label}
    </Typography>
  </Box>
);

const App = () => {
  const [selectedPage, setSelectedPage] = useState('Monitor');
  const [loggedIn, setLoggedIn] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);

  useEffect(() => {
    axios.get('/api/is_logged_in').then(r => {
      if (r.data.is_logged_in) setLoggedIn(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isMonitoring && selectedPage !== 'Monitor') setSelectedPage('Monitor');
  }, [isMonitoring]);

  const handlePageChange = (page) => {
    if (page === 'Admin') {
      if (!loggedIn) { alert('Please login to access Admin.'); return; }
      if (isMonitoring) { alert('Stop monitoring before accessing Admin.'); return; }
    }
    setSelectedPage(page);
  };

  const handleLogout = async () => {
    if (isMonitoring) { alert('Stop monitoring before logging out.'); return; }
    try {
      const r = await axios.post('/api/logout');
      if (r.data.message) { setLoggedIn(false); setSelectedPage('Monitor'); }
    } catch { alert('Logout failed. Please try again.'); }
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      <CssBaseline />

      {/* Sidebar */}
      <Box sx={S.sidebar}>
        {/* Logo */}
        <Box sx={S.logo}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
            Kitemon
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#475569', mt: 0.25, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Order Replication
          </Typography>
        </Box>

        {/* Nav */}
        <Box sx={{ flex: 1, pt: 1 }}>
          <NavItem
            icon={<ShowChartIcon />}
            label="Monitor"
            active={selectedPage === 'Monitor'}
            onClick={() => handlePageChange('Monitor')}
          />
          {loggedIn && (
            <NavItem
              icon={<TuneIcon />}
              label="Admin"
              active={selectedPage === 'Admin'}
              onClick={() => handlePageChange('Admin')}
            />
          )}
        </Box>

        {/* Bottom */}
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', p: 2 }}>
          {isMonitoring && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: '#22c55e', animation: 'pulse 2s infinite' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: 500 }}>Live</Typography>
            </Box>
          )}
          {loggedIn && (
            <Tooltip title="Logout" placement="right">
              <Box
                onClick={handleLogout}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer',
                  color: '#64748b', transition: 'all 0.15s',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#f87171' },
                }}
              >
                <LogoutIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '0.875rem' }}>Logout</Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Page Content */}
      <Box sx={{ ml: `${SIDEBAR_W}px`, flex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {selectedPage === 'Admin' && loggedIn && <Admin onSave={() => setLoggedIn(false)} />}
        {selectedPage === 'Monitor' && (
          <>
            {!loggedIn
              ? <Login setIsLoggedIn={setLoggedIn} />
              : <Monitor isMonitoring={isMonitoring} setIsMonitoring={setIsMonitoring} />
            }
          </>
        )}
      </Box>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </Box>
  );
};

export default App;
